import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { ABOUT_DATA, ABOUT_PAGE_DATA } from "@/data/siteData";
import {
  Lightbulb, Target, Handshake, Shield, BookOpen, Award,
  Eye, Crosshair, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

const iconMap = { Lightbulb, Target, Handshake, Shield, BookOpen, Award };

export default function AboutUs() {
  return (
    <div>
      <section className="relative py-24 md:py-32 overflow-hidden mesh-bg" data-testid="about-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider mb-6">
              {ABOUT_PAGE_DATA.hero.tagline}
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6">
              {ABOUT_PAGE_DATA.hero.titlePrefix} <span className="text-[#097DC3]">{ABOUT_PAGE_DATA.hero.titleHighlight}</span> {ABOUT_PAGE_DATA.hero.titleSuffix}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#475569] max-w-2xl">
              {ABOUT_PAGE_DATA.hero.subtitle}
            </p>
          </motion.div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#8CDDF6]/15 blur-3xl" />
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            <ScrollReveal>
              <div className="p-8 rounded-2xl bg-[#F8FAFC] border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#097DC3]/10 flex items-center justify-center mb-6">
                  <Eye size={24} className="text-[#097DC3]" />
                </div>
                <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-[#0A0A0A] mb-4">Our Vision</h2>
                <p className="text-[#475569] leading-relaxed">{ABOUT_DATA.vision}</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.15}>
              <div className="p-8 rounded-2xl bg-[#F8FAFC] border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#097DC3]/10 flex items-center justify-center mb-6">
                  <Crosshair size={24} className="text-[#097DC3]" />
                </div>
                <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-[#0A0A0A] mb-4">Our Mission</h2>
                <p className="text-[#475569] leading-relaxed">{ABOUT_DATA.mission}</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

<div   className="relative bg-fixed bg-center bg-cover"
  style={{
    backgroundImage: ABOUT_PAGE_DATA.differentiators.backgroundImage
  }}>
      <section className="py-24 md:py-32 bg-[#F8FAFC]/80" data-testid="differentiators-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{ABOUT_PAGE_DATA.differentiators.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-6">
              {ABOUT_PAGE_DATA.differentiators.title}
            </h2>
            <p className="text-base md:text-lg text-[#475569] max-w-2xl mb-16">
              {ABOUT_PAGE_DATA.differentiators.description}
            </p>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ABOUT_PAGE_DATA.differentiators.cards.map((card) => {
              const Icon = iconMap[card.icon];
              return (
                <StaggerItem key={card.title}>
                  <div className="card-lift bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-[#097DC3]/10 flex items-center justify-center mb-6">
                      <Icon size={24} className="text-[#097DC3]" />
                    </div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-2">{card.title}</h3>
                    <p className="text-sm text-[#475569] leading-relaxed">{card.description}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8FAFC]/80" data-testid="core-values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{ABOUT_PAGE_DATA.values.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-16">
              {ABOUT_PAGE_DATA.values.title}
            </h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ABOUT_DATA.values.map((val) => {
              const Icon = iconMap[val.icon];
              return (
                <StaggerItem key={val.title}>
                  <div className="card-lift bg-white p-8 rounded-2xl border border-slate-100 shadow-sm group">
                    <div className="w-12 h-12 rounded-xl bg-[#097DC3]/8 flex items-center justify-center mb-6 group-hover:bg-[#097DC3] transition-colors duration-300">
                      <Icon size={24} className="text-[#097DC3] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-2">{val.title}</h3>
                    <p className="text-sm text-[#475569] leading-relaxed">{val.description}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>
</div>

      <section className="py-24 md:py-32 bg-[#097DC3] relative overflow-hidden" data-testid="about-cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-white/20 blur-3xl -translate-x-1/2" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <ScrollReveal>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
              {ABOUT_PAGE_DATA.cta.title}
            </h2>
            <p className="text-lg text-[#8CDDF6] mb-10 max-w-xl mx-auto">
              {ABOUT_PAGE_DATA.cta.description}
            </p>
            <Link
              to="/contact"
              className="btn-pill inline-flex items-center gap-2 bg-white text-[#097DC3] px-8 py-3.5 rounded-full font-semibold text-sm"
              data-testid="about-cta-btn"
            >
              {ABOUT_PAGE_DATA.cta.button} <ArrowRight size={16} />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
